import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { RefreshCw, ChevronLeft, Search, Menu } from 'lucide-react';
import { AppHeaderActions } from './AppHeaderActions';
import { NotificationBell } from '../notifications/NotificationBell';
import { useSidebar } from '../ui/sidebar';
import { useDashboard } from '../../contexts/DashboardContext';
import { useBreadcrumbContext } from './AppBreadcrumb';
import { parseAppBar } from './sectionRegistry';
import { OPEN_SEARCH_EVENT } from '../ui/command-search';

/**
 * App bar — two modes (DESIGN_SYSTEM.md §5):
 *  - list:   indigo eyebrow + page title
 *  - detail: circular back button + breadcrumb (section eyebrow + record name)
 * Right side currently keeps the contextual actions (search/create/export) + bell;
 * Phase 4 trims it to search + divider + bell once the list toolbar owns create.
 */
export function AppHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toggleSidebar } = useSidebar();
  const { refreshDashboard, isRefreshing } = useDashboard();
  const { data: contextData } = useBreadcrumbContext();

  const bar = parseAppBar(location.pathname);
  const isDashboard = location.pathname === '/';

  const recordName =
    bar.action === 'create'
      ? `New ${bar.meta.label.replace(/s$/, '')}`
      : contextData.resourceName || (bar.recordId ? `#${bar.recordId}` : bar.meta.label);

  return (
    <header className="sticky top-0 z-[1000] border-b border-hairline bg-background">
      <div className="flex h-[72px] items-center justify-between gap-4 px-6">
        {/* LEFT — eyebrow+title (list) or back+breadcrumb (detail) */}
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {/* Mobile menu trigger — opens the off-canvas sidebar (DESIGN_SYSTEM §5).
              Hidden on desktop where the sidebar is always inline. In detail mode the
              back button below takes the leading slot, so the hamburger is list-only. */}
          {bar.mode !== 'detail' && (
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Open menu"
              className="flex size-9 shrink-0 items-center justify-center rounded-pill border border-hairline text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink md:hidden"
            >
              <Menu className="size-5" />
            </button>
          )}
          {bar.mode === 'detail' && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Back"
              className="flex size-9 shrink-0 items-center justify-center rounded-pill border border-hairline text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink"
            >
              <ChevronLeft className="size-4" />
            </button>
          )}
          <div className="min-w-0">
            {bar.mode === 'detail' ? (
              <Link
                to={`/${bar.slug}`}
                className="block text-[11.5px] font-semibold uppercase tracking-[0.07em] text-indigo hover:text-indigo-press"
              >
                {bar.meta.label}
              </Link>
            ) : (
              <div className="text-[11.5px] font-semibold uppercase tracking-[0.07em] text-indigo">
                {bar.meta.eyebrow}
              </div>
            )}
            <h1 className="truncate text-[19px] font-bold leading-tight text-ink">
              {bar.mode === 'detail' ? recordName : bar.meta.label}
            </h1>
          </div>
        </div>

        {/* RIGHT — contextual actions (temp) + search + divider + bell */}
        <div className="flex shrink-0 items-center gap-2">
          <AppHeaderActions />

          <button
            type="button"
            aria-label="Search"
            onClick={() => window.dispatchEvent(new Event(OPEN_SEARCH_EVENT))}
            className="inline-flex h-10 w-full max-w-[320px] sm:min-w-[260px] items-center gap-2 rounded-pill border border-hairline px-3.5 text-[13px] text-ink-2 transition-colors duration-150 hover:bg-surface-2 hover:text-ink"
          >
            <Search className="size-4 shrink-0" />
            <span className="hidden flex-1 text-left sm:inline">Search</span>
            <kbd className="hidden rounded bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-2 sm:inline">
              ⌘K
            </kbd>
          </button>

          {isDashboard && (
            <Button
              variant="ghost"
              size="icon"
              title="Refresh Dashboard"
              onClick={refreshDashboard}
              disabled={isRefreshing}
              className="size-9"
            >
              <RefreshCw className={`size-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          )}

          <Separator orientation="vertical" className="mx-1 h-6 bg-hairline" />

          <NotificationBell />
        </div>
      </div>
    </header>
  );
}
